import React, { useState, useCallback } from 'react';
import { Box, Paper, Typography, Button, TextField, IconButton, Tooltip, Snackbar, Alert, Grid } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhotoCameraBackIcon from '@mui/icons-material/PhotoCameraBack';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';
import './ImageToBase64.css';

function ImageToBase64() {
  const [base64, setBase64] = useState('');
  const [image, setImage] = useState(null);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      setError('Invalid file type. Please upload an image.');
      return;
    }
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
        setBase64(e.target.result);
        setError('');
      };
      reader.onerror = () => {
        setError('Failed to read the file.');
        setImage(null);
        setBase64('');
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
  });

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbarOpen(true);
  };

  const rawBase64 = base64.split(',')[1] || '';

  return (
    <>
      <SEO
        title="Image to Base64 Converter | Encode Images Online | UmbrellaCo"
        description="Convert images to Base64 strings or Data URIs. Free online encoder for embedding images directly into HTML, CSS, or JSON files."
        keywords="image to base64, base64 encoder, data uri generator, convert image online, embed image in html"
        url="/color-image-tools/image-to-base64"
        jsonLd={createSoftwareAppSchema(
          'Image to Base64 Converter',
          'Encode images to Base64 format and Data URIs instantly in your browser.',
          'https://umbrellaco.in/color-image-tools/image-to-base64',
          ['Image Encoding', 'Base64 Conversion', 'Developer Tools']
        )}
      />
      <ToolPageContainer
        title="Image to Base64 Converter"
        description="Upload an image to securely convert it into a Base64 string or Data URI directly in your browser. Perfect for inline image embedding."
      >
      
      {!image ? (
        <Box
          {...getRootProps()}
          className={`drop-zone ${isDragActive ? 'drop-zone-active' : ''}`}
          sx={{ mt: 2, p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <input {...getInputProps()} />
          <PhotoCameraBackIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography>Drag & drop an image here, or click to select</Typography>
          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        </Box>
      ) : (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6">Preview</Typography>
              <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
                <img src={image} alt="Preview" style={{ width: '100%', height: 'auto', borderRadius: '4px' }} />
              </Paper>
              <Button onClick={() => { setImage(null); setBase64(''); }} sx={{ mt: 1 }}>
                Convert Another Image
              </Button>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box>
                <Typography variant="h6">Data URI</Typography>
                <TextField
                  multiline
                  rows={4}
                  value={base64}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Tooltip title="Copy Data URI">
                        <IconButton onClick={() => handleCopy(base64)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Raw Base64</Typography>
                <TextField
                  multiline
                  rows={4}
                  value={rawBase64}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Tooltip title="Copy Raw Base64">
                        <IconButton onClick={() => handleCopy(rawBase64)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                  fullWidth
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
      </ToolPageContainer>
    </>
  );
}

export default ImageToBase64;